import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Gift, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { usePageMeta } from "../lib/seo";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function AuthPage() {
  usePageMeta("Inloggen of gratis registreren — VoltRoute", "Maak gratis een VoltRoute-account en plan direct je eerste elektrische roadtrip met slimme laadstops.");
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref");
  const planParam = searchParams.get("plan");
  const codeParam = searchParams.get("code");
  const nextParam = searchParams.get("next");
  const dest = nextParam && nextParam.startsWith("/") ? nextParam : (planParam ? `/dashboard?plan=${planParam}${codeParam ? `&code=${encodeURIComponent(codeParam)}` : ""}` : "/dashboard");
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [showRegPw, setShowRegPw] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(data.message, { duration: 8000 });
      setForgotMode(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      navigate(dest);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await register(regForm.name, regForm.email, regForm.password, refCode);
      if (data.referral_applied) toast.success("Gelukt! Jij én je vriend(in) hebben nu 1 maand Pro gratis 🎉");
      navigate(dest);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary-foreground" />
          </span>
          <span className="font-heading font-extrabold text-xl tracking-tight">VoltRoute</span>
        </Link>
        <div className="bg-card rounded-2xl p-8 shadow-[0_8px_32px_rgba(31,38,59,0.1)]">
          {refCode && (
            <div data-testid="referral-banner" className="mb-5 bg-primary/10 border border-primary/20 rounded-xl p-3.5 flex items-start gap-2.5">
              <Gift className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <p className="text-xs leading-relaxed"><strong>Je bent uitgenodigd!</strong> Maak een account aan en jullie krijgen <strong>allebei 1 maand Pro gratis</strong>.</p>
            </div>
          )}
          <Tabs defaultValue={refCode ? "register" : "login"}>
            <TabsList className="grid grid-cols-2 w-full rounded-full mb-6">
              <TabsTrigger data-testid="auth-tab-login" value="login" className="rounded-full">Inloggen</TabsTrigger>
              <TabsTrigger data-testid="auth-tab-register" value="register" className="rounded-full">Registreren</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              {forgotMode ? (
                <form onSubmit={handleForgot} className="space-y-4" data-testid="forgot-password-form">
                  <p className="text-sm text-muted-foreground">Vul je e-mailadres in — we sturen je een link om een nieuw wachtwoord in te stellen (1 uur geldig).</p>
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">E-mailadres</Label>
                    <Input id="forgot-email" data-testid="forgot-email-input" type="email" required value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)} placeholder="jij@voorbeeld.nl" />
                  </div>
                  <Button data-testid="forgot-submit-button" type="submit" disabled={loading}
                    className="w-full rounded-full bg-primary hover:bg-[#1B4332] active:scale-95 transition-[transform,background-color] duration-200">
                    {loading ? "Bezig..." : "Stuur herstel-link"}
                  </Button>
                  <button type="button" data-testid="forgot-back-button" onClick={() => setForgotMode(false)}
                    className="w-full text-xs font-semibold text-muted-foreground hover:text-primary transition-[color] duration-200">
                    Terug naar inloggen
                  </button>
                </form>
              ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">E-mailadres</Label>
                  <Input id="login-email" data-testid="login-email-input" type="email" required value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="jij@voorbeeld.nl" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Wachtwoord</Label>
                  <div className="relative">
                    <Input id="login-password" data-testid="login-password-input" type={showLoginPw ? "text" : "password"} required value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" className="pr-10" />
                    <button type="button" data-testid="login-password-toggle" onClick={() => setShowLoginPw((v) => !v)} tabIndex={-1}
                      aria-label={showLoginPw ? "Verberg wachtwoord" : "Toon wachtwoord"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-[color] duration-200">
                      {showLoginPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button data-testid="login-submit-button" type="submit" disabled={loading}
                  className="w-full rounded-full bg-primary hover:bg-[#1B4332] active:scale-95 transition-[transform,background-color] duration-200">
                  {loading ? "Bezig..." : "Inloggen"}
                </Button>
                <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotEmail(loginForm.email); setForgotMode(true); }}
                  className="w-full text-xs font-semibold text-muted-foreground hover:text-primary transition-[color] duration-200">
                  Wachtwoord vergeten?
                </button>
              </form>
              )}
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reg-name">Naam</Label>
                  <Input id="reg-name" data-testid="register-name-input" required value={regForm.name}
                    onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} placeholder="Jouw naam" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email">E-mailadres</Label>
                  <Input id="reg-email" data-testid="register-email-input" type="email" required value={regForm.email}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} placeholder="jij@voorbeeld.nl" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Wachtwoord (min. 6 tekens)</Label>
                  <div className="relative">
                    <Input id="reg-password" data-testid="register-password-input" type={showRegPw ? "text" : "password"} required minLength={6} value={regForm.password}
                      onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} placeholder="••••••••" className="pr-10" />
                    <button type="button" data-testid="register-password-toggle" onClick={() => setShowRegPw((v) => !v)} tabIndex={-1}
                      aria-label={showRegPw ? "Verberg wachtwoord" : "Toon wachtwoord"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-[color] duration-200">
                      {showRegPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button data-testid="register-submit-button" type="submit" disabled={loading}
                  className="w-full rounded-full bg-primary hover:bg-[#1B4332] active:scale-95 transition-[transform,background-color] duration-200">
                  {loading ? "Bezig..." : "Account aanmaken"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </motion.div>
    </div>
  );
}
